import * as THREE from 'three'
import type { GLTF } from 'three/examples/jsm/loaders/GLTFLoader.js'

/** complete GLB Geometry 复用诊断的全局开关名称。 */
const COMPLETE_GLB_GEOMETRY_REUSE_DEBUG_FLAG = '__PUPPYCAD_GLB_GEOMETRY_REUSE_DEBUG__'

/**
 * complete GLB 运行时 Geometry 复用诊断器。
 *
 * 诊断器只统计 GLTFLoader parser association 可追溯的原始 Mesh，并按 Geometry
 * 对象引用分组。拓扑 host、边线与编辑器辅助对象不会混入 glTF node 复用结果。
 */
export class CompleteGlbGeometryReuseDiagnostic {
  /**
   * 分析已实例化 complete GLB 的真实 Geometry 引用关系。
   *
   * @param gltf GLTFLoader 已完成实例化的原生结果。
   * @returns node occurrence 到共享 Geometry 的结构化报告。
   */
  public analyze(gltf: GLTF): PuppyCAD.CompleteGlbGeometryReuseReport {
    const associations = gltf.parser.associations as Map<
      THREE.Object3D,
      PuppyCAD.CompleteGlbParserAssociation | undefined
    >
    const sourceNodes = (gltf.parser.json as PuppyCAD.CompleteGlbJsonDocument).nodes ?? []
    const instantiatedNodeIndices = new Set<number>()
    const occurrencesByGeometry = new Map<
      THREE.BufferGeometry,
      PuppyCAD.CompleteGlbGeometryReuseOccurrence[]
    >()
    let excludedDerivedMeshCount = 0

    gltf.scene.traverse((object) => {
      const ownAssociation = associations.get(object)
      const ownNodeIndex = this.readNonNegativeInteger(ownAssociation?.nodes)
      if (ownNodeIndex !== null) {
        instantiatedNodeIndices.add(ownNodeIndex)
      }

      if (!(object instanceof THREE.Mesh)) {
        return
      }
      if (!ownAssociation) {
        excludedDerivedMeshCount += 1
        return
      }

      const nodeAssociation = this.resolveNodeAssociation(object, associations)
      const nodeIndex = this.readNonNegativeInteger(nodeAssociation?.nodes)
      const sourceNode = nodeIndex === null ? undefined : sourceNodes[nodeIndex]
      if (nodeIndex === null || !sourceNode) {
        excludedDerivedMeshCount += 1
        return
      }

      instantiatedNodeIndices.add(nodeIndex)
      const occurrences = occurrencesByGeometry.get(object.geometry) ?? []
      occurrences.push({
        nodeIndex,
        nodeName: sourceNode.name?.trim() || `Node ${nodeIndex}`,
        objectName: object.name?.trim() || '(unnamed Mesh)',
        objectUuid: object.uuid,
        meshDefinitionIndex: this.readNonNegativeInteger(ownAssociation.meshes)
          ?? this.readNonNegativeInteger(sourceNode.mesh),
        primitiveIndex: this.readNonNegativeInteger(ownAssociation.primitives),
      })
      occurrencesByGeometry.set(object.geometry, occurrences)
    })

    const sharedGeometryGroups = Array.from(occurrencesByGeometry.entries())
      .filter(([, occurrences]) => occurrences.length > 1)
      .map(([geometry, occurrences]): PuppyCAD.CompleteGlbSharedGeometryGroup => ({
        geometryUuid: geometry.uuid,
        instanceCount: occurrences.length,
        occurrences: [...occurrences].sort((left, right) => left.nodeIndex - right.nodeIndex),
      }))
      .sort((left, right) => (
        right.instanceCount - left.instanceCount
        || left.geometryUuid.localeCompare(right.geometryUuid)
      ))
    const meshInstanceCount = Array.from(occurrencesByGeometry.values())
      .reduce((total, occurrences) => total + occurrences.length, 0)
    const reusedMeshInstanceCount = sharedGeometryGroups
      .reduce((total, group) => total + group.instanceCount - 1, 0)

    return {
      instantiatedNodeCount: instantiatedNodeIndices.size,
      meshInstanceCount,
      uniqueGeometryCount: occurrencesByGeometry.size,
      sharedGeometryGroupCount: sharedGeometryGroups.length,
      reusedMeshInstanceCount,
      reuseRatio: meshInstanceCount > 0 ? reusedMeshInstanceCount / meshInstanceCount : 0,
      excludedDerivedMeshCount,
      sharedGeometryGroups,
    }
  }

  /**
   * 开关启用时输出一次 Geometry 复用报告。
   *
   * @param gltf GLTFLoader 已完成实例化的原生结果。
   * @returns 已输出的报告；开关关闭时返回 null。
   */
  public reportIfEnabled(gltf: GLTF): PuppyCAD.CompleteGlbGeometryReuseReport | null {
    if (!this.isEnabled()) {
      return null
    }

    const report = this.analyze(gltf)
    console.info('[cad-core] 完整 GLB Geometry 复用诊断', report)
    console.table(report.sharedGeometryGroups.map(group => ({
      geometryUuid: group.geometryUuid,
      instanceCount: group.instanceCount,
      nodeNames: group.occurrences
        .map(occurrence => `${occurrence.nodeName} (Node ${occurrence.nodeIndex})`)
        .join(', '),
      meshDefinitions: group.occurrences
        .map(occurrence => occurrence.meshDefinitionIndex ?? '-')
        .join(', '),
      primitiveIndices: group.occurrences
        .map(occurrence => occurrence.primitiveIndex ?? '-')
        .join(', '),
    })))
    return report
  }

  /**
   * 判断 Geometry 复用诊断是否开启。
   *
   * @returns 控制台全局开关是否为 truthy。
   */
  private isEnabled(): boolean {
    return Boolean(
      (globalThis as unknown as Record<string, unknown>)[COMPLETE_GLB_GEOMETRY_REUSE_DEBUG_FLAG],
    )
  }

  /**
   * 从 Mesh 自身或最近的 glTF node 祖先读取 node association。
   *
   * 多 primitive node 会由 Group 承载 node association、子 Mesh 承载 mesh/primitive
   * association，因此必须沿原始父链合并两类信息，才能输出正确 node 名称。
   *
   * @param object 当前原始渲染 Mesh。
   * @param associations GLTFLoader parser association 索引。
   * @returns 最近的有效 node association；不存在时返回 undefined。
   */
  private resolveNodeAssociation(
    object: THREE.Object3D,
    associations: ReadonlyMap<
      THREE.Object3D,
      PuppyCAD.CompleteGlbParserAssociation | undefined
    >,
  ): PuppyCAD.CompleteGlbParserAssociation | undefined {
    let current: THREE.Object3D | null = object
    while (current) {
      const association = associations.get(current)
      if (this.readNonNegativeInteger(association?.nodes) !== null) {
        return association
      }
      current = current.parent
    }
    return undefined
  }

  /**
   * 读取非负整数索引。
   *
   * @param value parser association 或 glTF definition 中的未知值。
   * @returns 有效索引；无效时返回 null。
   */
  private readNonNegativeInteger(value: unknown): number | null {
    return typeof value === 'number' && Number.isInteger(value) && value >= 0
      ? value
      : null
  }
}
